// 합의 관찰 조건과 게임 세력 보정. 서버 NatalCombinations.java 를 그대로 옮긴 것이다. 합화·격국 성패는 판정하지 않는다.
import { STEMS, BRANCHES, STEM_EL, EL_NAME, mod } from './calc.js';

const POS = ['년', '월', '일', '시'];
const GROUPS = [
  { kind: '방합', branches: [2, 3, 4], element: 0 }, { kind: '방합', branches: [5, 6, 7], element: 1 },
  { kind: '방합', branches: [8, 9, 10], element: 3 }, { kind: '방합', branches: [11, 0, 1], element: 4 },
  { kind: '삼합', branches: [8, 0, 4], element: 4 }, { kind: '삼합', branches: [11, 3, 7], element: 0 },
  { kind: '삼합', branches: [2, 6, 10], element: 1 }, { kind: '삼합', branches: [5, 9, 1], element: 3 },
];
const SIX = [[0, 1], [2, 11], [3, 10], [4, 9], [5, 8], [6, 7]];

const ev = (kind, label, status, detail) => ({ kind, label, status, detail });
const controls = (from, to) => mod(to - from, 5) === 2;
const containsAll = (pillars, branches) => branches.every((b) => pillars.some((p) => p && p.branch === b));
const branchText = (branches) => branches.map((b) => BRANCHES[b]).join('');
function positions(pillars, branches) {
  const result = [];
  for (let pos = 0; pos < pillars.length; pos++) {
    if (!pillars[pos]) continue;
    for (const b of branches) if (pillars[pos].branch === b) result.push(pos);
  }
  return result;
}
const hasClash = (pillars, pos) => pos.some((i) => pillars.some((o) => o && Math.abs(pillars[i].branch - o.branch) === 6));
const isStemPair = (a, b) => Boolean(a && b && Math.abs(a.stem - b.stem) === 5);
function blocked(pillars, a, b) {
  for (let mid = a + 1; mid < b; mid++) {
    if (!pillars[mid]) continue;
    const element = STEM_EL[pillars[mid].stem];
    if (controls(element, STEM_EL[pillars[a].stem]) || controls(element, STEM_EL[pillars[b].stem])) return true;
  }
  return false;
}

function applyStems(pillars, availability, evidence) {
  const partners = [0, 0, 0, 0];
  for (let a = 0; a < 4; a++) for (let b = a + 1; b < 4; b++) {
    if (isStemPair(pillars[a], pillars[b])) { partners[a]++; partners[b]++; }
  }
  for (let a = 0; a < 4; a++) for (let b = a + 1; b < 4; b++) {
    if (!isStemPair(pillars[a], pillars[b])) continue;
    const label = `천간합 ${POS[a]}${STEMS[pillars[a].stem]}·${POS[b]}${STEMS[pillars[b].stem]}`;
    if (a === 2 || b === 2) {
      evidence.push(ev('천간합', label, '일간의 합 · 합거 제외', '자평진전의 본신의 합 구별에 따라 일간과 상대 천간을 합거하지 않습니다.'));
    } else if (partners[a] > 1 || partners[b] > 1) {
      evidence.push(ev('천간합', label, '쟁합 후보 · 수치 보류', '둘 이상의 합 상대가 있어 단일 합으로 계산하지 않습니다.'));
    } else if (blocked(pillars, a, b)) {
      evidence.push(ev('천간합', label, '사이의 극 관계 · 수치 보류', '중간 천간의 극 관계가 있어 합거 보정을 보류합니다.'));
    } else {
      const distance = b - a === 1 ? 1 : b - a === 2 ? 0.5 : 0.25;
      availability[a] *= 1 - 0.15 * distance;
      availability[b] *= 1 - 0.15 * distance;
      evidence.push(ev('천간합', label, '결합 보정 적용', '거리와 사이의 방해를 구분해 두 천간의 가용 세력을 완만하게 줄입니다. 합화 판정은 아닙니다.'));
    }
  }
}

/** → { stems[4], branches[4][5], evidence[] } */
export function analyzeCombinations(chart) {
  const pillars = [chart.year, chart.month, chart.day, chart.hour];
  const stems = [1, 1, 1, 1];
  const branches = Array.from({ length: 4 }, () => new Array(5).fill(1));
  const evidence = [];
  applyStems(pillars, stems, evidence);
  const used = [false, false, false, false];
  for (const group of GROUPS) {
    if (!containsAll(pillars, group.branches)) continue; // 반합·없는 글자 보충 금지
    const pos = positions(pillars, group.branches);
    const label = `${group.kind} ${branchText(group.branches)}`;
    if (hasClash(pillars, pos)) {
      evidence.push(ev(group.kind, label, '충 동반 · 수치 보류', '합과 충의 해소 관계가 추가 판단을 요구하므로 이 합의 보정을 보류합니다.'));
      continue;
    }
    if (pos.some((p) => used[p])) {
      evidence.push(ev(group.kind, label, '중복 보정 제외', '같은 지지의 합 보정을 중복하지 않는 게임 규칙입니다.'));
      continue;
    }
    const increase = group.kind === '방합' ? 0.20 : 0.15;
    for (const p of pos) {
      used[p] = true;
      for (let element = 0; element < 5; element++) {
        branches[p][element] = element === group.element ? 1 + increase : 1 - increase / 2;
      }
    }
    evidence.push(ev(group.kind, label, '세력 보정 적용',
      `세 글자가 모두 있습니다. 기존 지장간 중 ${EL_NAME[group.element]}의 비중을 게임 수치로 보정합니다. 다른 오행으로 합화한 판정은 아닙니다.`));
  }
  for (const pair of SIX) {
    if (!containsAll(pillars, pair)) continue;
    const pos = positions(pillars, pair);
    const label = `육합 ${branchText(pair)}`;
    if (pos.some((p) => used[p])) {
      evidence.push(ev('육합', label, '중복 보정 제외', '방합·삼합에 반영한 지지에는 육합 보정을 다시 더하지 않습니다.'));
    } else if (hasClash(pillars, pos)) {
      evidence.push(ev('육합', label, '충 동반 · 수치 보류', '합과 충의 해소 관계는 추가 판단이 필요합니다.'));
    } else {
      for (const p of pos) { used[p] = true; branches[p].fill(0.95); }
      evidence.push(ev('육합', label, '결합 보정 적용', '두 지지의 결합을 가용 세력 0.95배로 환산합니다. 오행 전환·길흉 확정은 하지 않습니다.'));
    }
  }
  return { stems, branches, evidence };
}
